import type { OceanDetail } from "./ocean-mesh.ts";

/** Preset ids, cheapest first. */
export const GRAPHICS_PRESET_IDS = ["low", "balanced", "ultra"] as const;
export type GraphicsPreset = (typeof GRAPHICS_PRESET_IDS)[number];

/** Ocean cost: LOD mesh rings, wave components shading each water pixel, and frames per wake-simulation step. */
export interface OceanQuality extends OceanDetail {
  normals: number;
  wakeEvery: 1 | 2;
}

/**
 * Visual-cost controls. Balanced and Ultra retain the same art, lighting and effects; Low is
 * Balanced without the ocean's reflection and refraction captures (the water reflects the sky
 * and shades depth from the sea bed instead of mirroring the scene and showing what lies below),
 * with a smaller 2 m ocean grid, the 20 longest wave components in its normals and the wake
 * simulation stepping every other frame. Ultra widens the ocean's finer rings.
 */
export const GRAPHICS_PRESETS: Record<GraphicsPreset, {
  label: string; maxPixelRatio: number; captures: boolean; captureScale: number; cloudSteps: 48 | 96; shadowMapSize: number; samples: number;
  ocean: OceanQuality;
}> = {
  low: { label: "Low", maxPixelRatio: 1, captures: false, captureScale: 0.5, cloudSteps: 48, shadowMapSize: 2048, samples: 4,
    ocean: { near: 192, ring: 16, normals: 20, wakeEvery: 2 } },
  balanced: { label: "Balanced", maxPixelRatio: 1, captures: true, captureScale: 0.5, cloudSteps: 48, shadowMapSize: 2048, samples: 4,
    ocean: { near: 320, ring: 24, normals: 32, wakeEvery: 1 } },
  ultra: { label: "Ultra", maxPixelRatio: 2, captures: true, captureScale: 1, cloudSteps: 96, shadowMapSize: 4096, samples: 8,
    ocean: { near: 384, ring: 32, normals: 32, wakeEvery: 1 } },
};

/** A select or stored value as a preset; anything unknown is Balanced. */
export function graphicsPreset(value: string): GraphicsPreset {
  return (GRAPHICS_PRESET_IDS as readonly string[]).includes(value) ? value as GraphicsPreset : "balanced";
}

/** Independent contact-shading comparison; neither graphics preset overrides it. */
export type ContactShadowSamples = 24 | 48;
